<template>
  <article>
    <div :id="routeOverview" class="container-fixed offset-top-2 offset-bottom-1">
      <slot name="header" />

      <VSearchFormStatus v-bind="searchform" spinner class="offset-top-1" />
    </div>

    <div v-if="table.rows" class="container-fluid grayed-offset">
      <div class="container-fixed">
        <h3 class="offset-bottom-2" v-html="$t('barring.history_overview_outdated_versions')"></h3>

        <transition name="fade" mode="out-in" appear>
          <VueGoodTable v-bind="table">
            <template #table-row-after="{ row, index }">
              <td>
                <VButton class="btn btn-default btn-small" label="barring.history_3rd_party_partner_details_button_label" @click.stop="selectRow(row, index)" />
              </td>
            </template>
            <template #table-column="{ column }">
              <VTableHeader :columns="table.columns" :column="column" locale-namespace="barring" />
            </template>
          </VueGoodTable>
        </transition>
      </div>
    </div>

    <div :id="routeDetails" class="container-fixed">
      <transition name="fade" mode="out-in" appear>
        <VSpinner v-if="activeElement && isLoading" key="loading" class="offset-top-2" />
        <div v-else-if="items" key="items" class="offset-bottom-3">
          <slot
            name="details"
            :validFrom="formatDateTime(activeElement.validFrom)"
            :validTo="formatDateTime(activeElement.validTo)"
            :items="items"
            :itemsWithExceptions="itemsWithExceptions"
            :itemsWithoutExceptions="itemsWithoutExceptions"
            :resellingItems="resellingItems"
            :factoringItems="factoringItems"
            :voiceFactoringItems="voiceFactoringItems"
            :voiceFactoringExceptions="voiceFactoringExceptions"
            :isResellingBarred="isResellingBarred"
            :isFactoringBarred="isFactoringBarred"
            :isVoiceFactoringBarred="isVoiceFactoringBarred"
            :hasVoiceFactoringExceptions="hasVoiceFactoringExceptions"
            :isProfileTypeCategories="isProfileTypeCategories"
          />
        </div>
      </transition>
    </div>
  </article>
</template>

<script>
import BarringStatusMixin from '@/mixins/barringStatus';
import { formatDateTime } from '@/utils/date';
import VSpinner from '@/components/Spinner';
import VButton from '@/components/Button';
import VSearchFormStatus from '@/components/SearchFormStatus';
import TitleMixin from '@/mixins/title';
import SearchFormMixin from '@/mixins/searchform';
import TableMixin from '@/mixins/table';
import TableUtilitiesMixin from '@/mixins/tableUtilities';
import Proxy from '@/proxies/BarringProxy';

export default {
  name: 'BarringHistory',

  components: {
    VueGoodTable: () => import('vue-good-table').then(m => m.VueGoodTable),
    VButton,
    VSearchFormStatus,
    VTableHeader: () => import('@/components/Table/Header'),
    VSpinner,
  },

  mixins: [TitleMixin, SearchFormMixin, TableMixin, TableUtilitiesMixin, BarringStatusMixin],

  props: {
    businessModel: {
      type: String,
      required: true,
    },

    routeOverview: {
      type: String,
      required: true,
    },

    routeDetails: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      formatDateTime,
      isLoading: true,

      table: {
        columns: [
          {
            label: 'barring.history_valid_from_date_column_heading',
            field: 'tableValidFrom',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'barring.history_valid_to_date_column_heading',
            field: 'tableValidTo',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'barring.history_input_channel_column_heading',
            field: 'inputChannel',
            filterable: true,
            sortable: true,
            placeholder: ' ',
          },
          {
            label: 'barring.history_action_column_heading',
            field: '',
            filterable: false,
            sortable: false,
            placeholder: ' ',
          },
        ],
        rows: null,
      },
    };
  },

  computed: {
    selected() {
      return this.$route.params.selectedIndex === undefined ? null : Number(this.$route.params.selectedIndex);
    },

    activeElement() {
      return this.table?.rows?.[this.selected];
    },
  },

  watch: {
    activeElement: {
      immediate: true,
      handler: 'loadDetails',
    },
  },

  async created() {
    this.searchLoading();
    const response = await new Proxy().getProfileHistory();

    if (response.length === 0) {
      this.searchErrorNoData();
    } else {
      this.table.rows = this.translateRows(response);
      this.handleDefaultSearchFields(this.table);
      this.searchSuccess();
    }
  },

  methods: {
    translateRows(rows) {
      rows.map(row => {
        if (row.validFrom) row.tableValidFrom = formatDateTime(row.validFrom);
        if (row.validTo) row.tableValidTo = formatDateTime(row.validTo);
        return row;
      });
      return rows;
    },

    selectRow(row, index) {
      this.setAriaSelected(index);

      const selectedIndex = row.originalIndex;
      if (this.selected === selectedIndex) {
        this.$router.push({ name: this.routeOverview });
      } else {
        this.$router.push({
          name: this.routeDetails,
          params: {
            selectedIndex: row.originalIndex,
          },
        });
      }
    },

    tableRowStyle(row) {
      if (this.selected === row.originalIndex) {
        return 'brand';
      }
      return '';
    },

    async loadDetails() {
      this.items = null;
      if (!this.activeElement) return;
      this.isLoading = true;
      const response = await new Proxy().getHistoryDetails(this.businessModel, this.activeElement.validFrom);
      this.parseResponse(response);
      this.isLoading = false;
    },
  },
};
</script>
